import * as d3 from 'd3'
import _ from 'lodash'

import {Orientations} from '@/components/plots/enums'

export class CurveType {
  static get default() { return d3.curveLinear }
  static get linear() { return d3.curveLinear }
  static get spline() { return d3.curveCatmullRom }
  static get stepped() { return d3.curveStepBefore }
}

export class LineEffect {
  static get dashed() { return {w: 2, c: 'sqaure', d: '12, 4'} }
  static get default() { return {w: 2, c: 'sqaure', d: 0} }
  static get dotted() { return {w: 4, c: 'round', d: '0, 10'} }
  static get solid() { return {w: 2, c: 'sqaure', d: 0} }
}

/**
 * Generates closed paths from a list of scaled point lists
 * (typically generated by points method).
 * @y The data for the y axis
 * @axis_y The y_axis object
 * @curve_type The d3 curve function to apply to the path
 */
export const areas = (y, axis_y, points, curve_type = CurveType.Linear) => {
  if(y && axis_y) {
    return points.map(p => {
      return d3.area()
        .curve(curve_type)
        .x(d => d[0])
        .y0(axis_y.at(0))
        .y1(d => d[1])(p)
    })
  }
  return []
}

/**
 * Generates a list of rectangles for use in a horizontal or vertical bar chart
 * @x The data for the x axis
 * @y The data for the y axis
 * @band_scale The scale representing the categorical variable
 * @cont_scale The scale representing the continuous variable
 * @orientation The orientation of the bar
 */
export const bars = (x, y, band_scale, cont_scale, orientation) => {
  if(!x || !y || !band_scale || !cont_scale) return []
  if(!band_scale.paddingInner) return []
  if(orientation === Orientations.Horizontal) {
    return x.index.map((xi, i) => {
      const bw = band_scale.bandwidth()
      return {
        x: 0,
        y: band_scale(xi),
        width: Math.max(0, cont_scale(y.data[i])),
        height: Math.max(0, bw)
      }
    })
  }

  if(orientation === Orientations.Vertical) {
    return x.index.map((xi, i) => {
      const bw = band_scale.bandwidth()
      const yi = cont_scale(y.data[i][0])
      const h = Math.max(0, cont_scale(0) - yi)
      return {
        x: band_scale(xi),
        y: yi,
        width: Math.max(0, bw),
        height: h
      }
    })
  }
}

/**
 * TODO: Move this into another file specific to data transformation
 */
export const bin = (x, y, bin_count) => {
  const binned = y.data.map((yi, i) => {
    return d3.bin().thresholds(bin_count)(yi)
  })

  const x_data = {
    ...x,
    data: binned.map(xi => {
      return xi.reduce((acc, b, i) => {
        acc.push(b.x0)
        return acc
      }, [])
    })
  }

  const y_data = {
    ...y,
    data: binned.map(yi => {
      return yi.reduce((acc, b, i) => {
        acc.push(b.length)
        return acc
      }, [])
    })
  }

  return {
    binned,
    x_data,
    y_data
  }
}

/**
 * Read effects from supplied data and return array of effects type
 * In some cases it is possible for y.effects to be a string or an array
 * If no effects are present return default
 * @y The data for the y axis
 * @effect_type The type of effect to parse
 */
export const effects = (y, effect_type) => {
  if(typeof y.effects === 'string') {
    return new Array(y.data.length).fill(effect_type[y.effects] || effect_type.default)
  }

  return new Array(y.data.length)
    .fill(effect_type.default)
    .map((e, i) => (y.effects && y.effects[i]) ?
      effect_type[y.effects[i]] : effect_type.default)
}

/**
 * Generates a list of rectangle lists for use in a grouped bar chart
 * @band_scale The scale representing the categorical variable
 * @cont_scale The scale representing the continuous variable
 * @orientation The orientation of the bar
 */
export const grouped_bars = (x, y, band_scale, cont_scale, orientation) => {
  if(!x || !y || !band_scale || !cont_scale) return []
  if(!band_scale || !band_scale.paddingInner) return []
  if(orientation === Orientations.Horizontal) {
    const group_scale =
      d3.scaleBand()
        .domain(y.data[0].map((x, i) => i))
        .range([0, band_scale.bandwidth()])
        .padding(0.05)

    return x.index.reduce((acc, xi, i) => {
      return acc.concat(
        y.data[i].map((y, j, d) => {
          return {
            i: j,
            y: band_scale(xi) + group_scale(j),
            x: cont_scale(0),
            height: group_scale.bandwidth(),
            width: cont_scale(y) + cont_scale(0)
          }
        })
      )
    }, [])
  }

  if(orientation === Orientations.Vertical) {
    const group_scale =
      d3.scaleBand()
        .domain(y.data[0].map((x, i) => i))
        .range([0, band_scale.bandwidth()])
        .padding(0.05)

    return x.index.reduce((acc, x, i) => {
      return acc.concat(
        y.data[i].map((y, j, d) => {
          return {
            i: j,
            y: cont_scale(y),
            x: band_scale(x) + group_scale(j),
            height: cont_scale(0) - cont_scale(y),
            width: group_scale.bandwidth()
          }
        })
      )
    }, [])
  }
}

/**
 * Generate a list of rectangles and their color encodings and values
 * @x The data for the x axis
 * @y The data for the y axis
 * @axis_x The x axis
 * @axis_y The y axis
 */
export const heatmap = (x, y, axis_x, axis_y) => {
  if(!x || !y || !axis_x || !axis_y) return []
  const scale_z =
    d3.scaleLinear()
      .domain(d3.extent(d3.merge(y.data)))
      .range([0, 1])

  return x.index.reduce((acc, x, i) => {
    y.index.reduce((acc, yi, j) => {
      acc.push({
        y: Math.ceil(axis_y.at(yi)),
        x: Math.ceil(axis_x.at(x)),
        w: Math.ceil(axis_x.scale.bandwidth()),
        h: Math.ceil(axis_y.scale.bandwidth()),
        v: y.data[j][i],
        o: scale_z(y.data[j][i])
      })
      return acc
    }, acc)
    return acc
  }, [])
}

/**
 * Generate a list of hist bars
 * @bins Binned data
 * @axis_x The x axis
 * @axis_y The y axis
 */
export const histogram = (bins, axis_x, axis_y) => {
  if(!axis_x || !axis_y) return []

  return bins.map(bin => {
    return bin.reduce((acc, b) => {
      acc.push({
        x: axis_x.at(b.x0),
        y: axis_y.at(b.length),
        height: axis_y.at(0) - axis_y.at(b.length),
        width: Math.max(axis_x.at(b.x1) - axis_x.at(b.x0) - 1)
      })
      return acc
    }, [])
  })
}

/**
 * Generates a list of lines from a list of scaled point lists
 * @points A list of scaled point lists
 * @curve_type One of the CurveType constants
 */
export const lines = (points, curve_type) => {
  return points.reduce((lines, points) => {
    lines.push(
      d3.line()
        .curve(curve_type || CurveType.default)
        .defined(p => p[0] !== null && p[1] !== null)
        .x((p, i) => p[0])
        .y((p, i) => p[1])(points))
    return lines
  }, [])
}

/**
 * Generates a scaled list of point lists from the Candela data format
 * @x The data for the x axis
 * @y The data for the y axis
 * @axis_x The x axis
 * @axis_y The y axis
 */
export const points = (x, y, axis_x, axis_y) => {
  if(x && y && axis_x && axis_y) {
    return y.data.reduce((points, yi, i) => {
      points.push(
        d3.zip(
          (x.data[i] || x.data[0]).map(x => axis_x.at(x)),
          yi.map(y => axis_y.at(y))
        )
      )
      return points
    }, [])
  }
  return []
}

/**
 * Parse point visibility from supplied data and return an array of booleans
 * indicating point visibility for each row of data
 * d.points can be either a boolean or an array of booleans
 * If no point visibility value is present on the supplied data
 * return an array of false values for each row
 *
 * @y The data for the y axis
 */
export const point_visibility = y => {
  if(!y || !y.points) return []

  if(_.isArray(y.points)) {
    return y.data.reduce((acc, yi, i) => {
      acc.push(y.points[i] || false)
      return acc
    }, [])
  }

  if(!y.points || typeof y.points === 'boolean') {
    return new Array(y.data.length).fill(y.points || false)
  }
}

export const stacked_bars = (x, y, band_scale, cont_scale, orientation) => {
  if(!x || !y || !band_scale || !cont_scale) return []
  if(!band_scale || !band_scale.paddingInner) return []

  const data = x.index.reduce((acc, xi, i) => {
    const d = acc[i] = acc[i] || {xi}
    y.index.forEach((yi, j) => {
      d[yi] = y.data[i][j]
    })
    return acc
  }, [])

  const stack_data = d3
    .stack()
    .keys(y.index)(data)

  const stack_scale = cont_scale.copy()
  stack_scale.domain([
    0, d3.max(stack_data, d => d3.max(d, d => d[1]))
  ])

  if(orientation === Orientations.Horizontal) {
    return stack_data.flatMap((a, i) => {
      return a.map((b, j) => {
        return {
          i,
          x: stack_scale(b[0]),
          y: band_scale(b.data.xi),
          width: stack_scale(b[1]) - stack_scale(b[0]),
          height: band_scale.bandwidth()
        }
      })
    })
  }

  if(orientation === Orientations.Vertical) {
    return stack_data.flatMap((a, i) => {
      return a.map((b, j) => {
        return {
          i,
          x: band_scale(b.data.xi),
          y: stack_scale(b[1]),
          width: band_scale.bandwidth(),
          height: stack_scale(b[0]) - stack_scale(b[1])
        }
      })
    })
  }
}
